/**
 * /dashboard/copytraders — manage copy-trading subscriptions.
 *
 * v6-styled wrapper around the existing /portfolio/copy data path
 * (founder direction 2026-05-24: "apply the design you added, just
 * wire to make functional" + "i do want all these important data
 * and information with the new design").
 *
 * Each row mirrors the v1 page's full data:
 *   - Allocation + Committed (with %)
 *   - Total P/L (absolute + %, color-coded)
 *   - Progress bar (committed vs allocation)
 *   - Activity / Edit / Pause+Resume / Unsubscribe actions
 *
 * Toggle for cancelled subs at the bottom. Separate copy-trade
 * transactions section below the list (founder direction: copy-trade
 * fills live here, not in /dashboard/transactions).
 */
"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Plus } from "lucide-react";
import {
  api,
  loadTokens,
  type CopySubscription,
  type CopySubscriptionStats,
  type CopySubscriptionTrade,
} from "@/lib/api";
import { DashboardSubpageShell } from "@/components/v6/dashboard/DashboardSubpageShell";
import { SubscribeToPiModal } from "@/components/SubscribeToPiModal";

interface FlatTrade {
  subId: string;
  piUsername: string;
  trade: CopySubscriptionTrade;
}

type StatsBySub = Record<string, CopySubscriptionStats | null>;

export default function CopytradersPage() {
  const [subs, setSubs] = useState<CopySubscription[] | null>(null);
  const [stats, setStats] = useState<StatsBySub>({});
  const [trades, setTrades] = useState<FlatTrade[]>([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 2026-05-24 — Edit opens the SubscribeToPiModal in edit mode
  // (same pattern v1 /portfolio/copy uses). When the modal saves,
  // it returns the updated row; we swap it into local state so the
  // card re-renders with new allocation / filters immediately.
  const [editing, setEditing] = useState<CopySubscription | null>(null);

  useEffect(() => {
    if (!loadTokens()) {
      setSubs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await api.listMyCopySubscriptions(showCancelled);
        if (cancelled) return;
        setSubs(rows);

        // Per-sub stats + recent trades, fanned out in parallel.
        const active = rows.filter((s) => !s.unsubscribedAt);
        const [statsResults, tradeBatches] = await Promise.all([
          Promise.all(
            active.map((s) =>
              api
                .getCopySubscriptionStats(s.id)
                .then((st) => [s.id, st] as const)
                .catch(() => [s.id, null] as const),
            ),
          ),
          Promise.all(
            active.map((s) =>
              api
                .listCopySubscriptionTrades(s.id, 8)
                .then((ts) =>
                  ts.map<FlatTrade>((trade) => ({
                    subId: s.id,
                    piUsername: s.piUsername,
                    trade,
                  })),
                )
                .catch(() => [] as FlatTrade[]),
            ),
          ),
        ]);
        if (cancelled) return;

        const statsMap: StatsBySub = {};
        for (const [id, st] of statsResults) statsMap[id] = st;
        setStats(statsMap);

        const flat = tradeBatches
          .flat()
          .sort(
            (a, b) =>
              new Date(b.trade.createdAt).getTime() -
              new Date(a.trade.createdAt).getTime(),
          );
        setTrades(flat.slice(0, 15));
      } catch (e: unknown) {
        if (!cancelled) {
          setSubs([]);
          setError(
            e instanceof Error ? e.message : "Failed to load copytraders",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCancelled]);

  async function handlePauseResume(sub: CopySubscription) {
    setBusyId(sub.id);
    try {
      const next = sub.paused
        ? await api.resumeCopySubscription(sub.id)
        : await api.pauseCopySubscription(sub.id);
      setSubs((prev) =>
        (prev ?? []).map((s) => (s.id === sub.id ? next : s)),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Pause/resume failed");
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnsubscribe(sub: CopySubscription) {
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Unsubscribe from @${sub.piUsername}?`)
    ) {
      return;
    }
    setBusyId(sub.id);
    try {
      await api.unsubscribeFromPi(sub.id);
      setSubs((prev) => (prev ?? []).filter((s) => s.id !== sub.id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Unsubscribe failed");
    } finally {
      setBusyId(null);
    }
  }

  const active = (subs ?? []).filter((s) => !s.unsubscribedAt);
  const totalAllocCents = active.reduce(
    (sum, s) => sum + Number(s.allocationCents),
    0,
  );
  const totalCommittedCents = active.reduce(
    (sum, s) => sum + Number(s.committedCents),
    0,
  );
  const totalPnlCents = active.reduce(
    (sum, s) => sum + Number(stats[s.id]?.totalPnlCents ?? 0),
    0,
  );

  return (
    <DashboardSubpageShell
      title="My copytraders"
      subtitle="Every PI you're mirroring. Edit allocation or filters, pause to stop new copies (existing positions stay open), or unsubscribe to end the chain."
    >
      {/* Summary tiles + Find-PIs CTA */}
      <section className="mb-5 flex flex-wrap items-stretch gap-3">
        <SummaryTile
          label="Total allocation"
          value={`$${(totalAllocCents / 100).toFixed(2)}`}
        />
        <SummaryTile
          label="Currently committed"
          value={`$${(totalCommittedCents / 100).toFixed(2)}`}
          sub={
            totalAllocCents > 0
              ? `${((totalCommittedCents / totalAllocCents) * 100).toFixed(0)}% of allocation`
              : "—"
          }
        />
        <SummaryTile
          label="Total P/L"
          value={`${totalPnlCents >= 0 ? "+" : "−"}$${(Math.abs(totalPnlCents) / 100).toFixed(2)}`}
          tone={totalPnlCents > 0 ? "yes" : totalPnlCents < 0 ? "no" : undefined}
        />
        <Link
          href="/leaderboard"
          className="ml-auto inline-flex items-center gap-2 self-start rounded-lg border px-4 py-2.5 text-[13px] font-semibold"
          style={{
            borderColor: "var(--color-border)",
            color: "var(--color-text)",
            background: "var(--color-surface)",
          }}
        >
          <Plus size={14} strokeWidth={2} />
          Find PIs to copy
        </Link>
      </section>

      {error && (
        <div
          className="mb-3 rounded-md border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--color-no)",
            background: "color-mix(in srgb, var(--color-no) 8%, transparent)",
            color: "var(--color-no)",
          }}
        >
          {error}
        </div>
      )}

      <h2
        className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: "var(--color-text3)" }}
      >
        Copy subscriptions
      </h2>

      {subs === null ? (
        <CopySkeleton />
      ) : subs.length === 0 ? (
        <div
          className="rounded-xl border px-5 py-10 text-center text-[14px]"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text2)",
          }}
        >
          You don&apos;t mirror any traders yet.{" "}
          <Link
            href="/leaderboard"
            className="font-semibold"
            style={{ color: "var(--color-accent)" }}
          >
            Browse top traders →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {subs.map((s) => (
            <CopyCard
              key={s.id}
              sub={s}
              stats={stats[s.id] ?? null}
              busy={busyId === s.id}
              onEdit={() => setEditing(s)}
              onPauseResume={() => handlePauseResume(s)}
              onUnsubscribe={() => handleUnsubscribe(s)}
            />
          ))}
        </div>
      )}

      {/* Edit modal — opens when the user taps "Edit" on any card.
       * Re-uses the v1 SubscribeToPiModal so the editing flow + form
       * validation + save handler stays consistent across surfaces. */}
      {editing && (
        <SubscribeToPiModal
          piUsername={editing.piUsername}
          existing={editing}
          onClose={() => setEditing(null)}
          onSuccess={(updated) => {
            setSubs((prev) =>
              (prev ?? []).map((s) => (s.id === editing.id ? updated : s)),
            );
            setEditing(null);
          }}
        />
      )}

      {/* Cancelled toggle */}
      <label className="mt-5 flex cursor-pointer items-center gap-2 text-[12px]">
        <input
          type="checkbox"
          checked={showCancelled}
          onChange={(e) => setShowCancelled(e.target.checked)}
        />
        <span style={{ color: "var(--color-text2)" }}>
          Show cancelled subscriptions
        </span>
      </label>

      {/* Copy-trade transactions section */}
      <h2
        className="mb-3 mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: "var(--color-text3)" }}
      >
        Copy-trade transactions · recent
      </h2>
      {trades.length === 0 ? (
        <div
          className="rounded-xl border px-5 py-8 text-center text-[13px]"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text3)",
          }}
        >
          No copy-trade activity yet.
        </div>
      ) : (
        <ul
          className="overflow-hidden rounded-xl border"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
          }}
        >
          {trades.map((row, idx) => {
            const positive = row.trade.side === "sell";
            const filled = Number(row.trade.filledShares ?? 0) / 100;
            const px = row.trade.avgFillPriceCents ?? 0;
            const notionalCents = Math.round(filled * px);
            return (
              <li
                key={`${row.subId}-${row.trade.orderId}`}
                className="flex items-center gap-3 px-4 py-3.5"
                style={{
                  borderTop:
                    idx === 0 ? "none" : "1px solid var(--color-border)",
                }}
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: positive
                      ? "color-mix(in srgb, var(--color-yes) 14%, transparent)"
                      : "color-mix(in srgb, var(--color-no) 14%, transparent)",
                    color: positive
                      ? "var(--color-yes)"
                      : "var(--color-no)",
                  }}
                >
                  {positive ? (
                    <ArrowUpRight size={16} strokeWidth={2} />
                  ) : (
                    <ArrowDownRight size={16} strokeWidth={2} />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold">
                    @{row.piUsername} ·{" "}
                    {row.trade.side === "buy" ? "Bought" : "Sold"}{" "}
                    {filled.toFixed(2)} shares
                  </div>
                  <div
                    className="mt-0.5 truncate text-[11px]"
                    style={{ color: "var(--color-text3)" }}
                  >
                    {row.trade.marketTitle?.en ?? row.trade.marketSlug} ·{" "}
                    {row.trade.status} ·{" "}
                    {formatRelative(row.trade.createdAt)}
                  </div>
                </div>
                <span
                  className="flex-shrink-0 font-mono text-[14px] font-bold tabular-nums"
                  style={{
                    color: positive ? "var(--color-yes)" : "var(--color-no)",
                  }}
                >
                  {positive ? "+" : "−"}$
                  {(notionalCents / 100).toFixed(2)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardSubpageShell>
  );
}

function SummaryTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "yes" | "no";
}) {
  return (
    <div
      className="flex-1 min-w-[180px] rounded-xl border p-4"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <div
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "var(--color-text3)" }}
      >
        {label}
      </div>
      <div
        className="mt-1 font-mono text-[22px] font-bold tabular-nums tracking-[-0.02em]"
        style={{
          color:
            tone === "yes"
              ? "var(--color-yes)"
              : tone === "no"
                ? "var(--color-no)"
                : "var(--color-text)",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          className="mt-0.5 text-[11px]"
          style={{ color: "var(--color-text3)" }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function CopyCard({
  sub,
  stats,
  busy,
  onEdit,
  onPauseResume,
  onUnsubscribe,
}: {
  sub: CopySubscription;
  stats: CopySubscriptionStats | null;
  busy: boolean;
  onEdit: () => void;
  onPauseResume: () => void;
  onUnsubscribe: () => void;
}) {
  const alloc = Number(sub.allocationCents);
  const committed = Number(sub.committedCents);
  const committedPct = alloc > 0 ? (committed / alloc) * 100 : 0;
  const pnl = Number(stats?.totalPnlCents ?? 0);
  const pnlPct = stats?.pnlPct ?? null;
  const cancelled = !!sub.unsubscribedAt;

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border p-4"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
        opacity: cancelled ? 0.6 : 1,
      }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <Link
          href={`/u/${sub.piUsername}`}
          className="text-[15px] font-bold no-underline"
          style={{ color: "var(--color-text)" }}
        >
          @{sub.piUsername}
        </Link>
        <span
          className="rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={{
            background: cancelled
              ? "var(--color-surface2)"
              : sub.paused
                ? "color-mix(in srgb, var(--color-text3) 14%, transparent)"
                : "color-mix(in srgb, var(--color-yes) 14%, transparent)",
            color: cancelled
              ? "var(--color-text3)"
              : sub.paused
                ? "var(--color-text2)"
                : "var(--color-yes)",
          }}
        >
          {cancelled ? "Cancelled" : sub.paused ? "Paused" : "Active"}
        </span>
      </div>

      {/* Allocation + Committed grid */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--color-text3)" }}
          >
            Allocation
          </div>
          <div className="mt-0.5 font-mono text-[15px] font-bold tabular-nums">
            ${(alloc / 100).toFixed(2)}
          </div>
        </div>
        <div>
          <div
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--color-text3)" }}
          >
            Committed
          </div>
          <div className="mt-0.5 font-mono text-[15px] font-bold tabular-nums">
            ${(committed / 100).toFixed(2)}{" "}
            <span
              className="text-[11px] font-medium"
              style={{ color: "var(--color-text3)" }}
            >
              ({committedPct.toFixed(0)}%)
            </span>
          </div>
        </div>
      </div>

      {/* Total P/L */}
      <div>
        <div
          className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: "var(--color-text3)" }}
        >
          Total P/L
        </div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <span
            className="font-mono text-[15px] font-bold tabular-nums"
            style={{
              color: !stats
                ? "var(--color-text3)"
                : pnl > 0
                  ? "var(--color-yes)"
                  : pnl < 0
                    ? "var(--color-no)"
                    : "var(--color-text)",
            }}
          >
            {!stats
              ? "—"
              : `${pnl >= 0 ? "+" : "−"}$${(Math.abs(pnl) / 100).toFixed(2)}`}
          </span>
          {pnlPct != null && (
            <span
              className="font-mono text-[11px] font-semibold"
              style={{
                color:
                  pnlPct >= 0 ? "var(--color-yes)" : "var(--color-no)",
              }}
            >
              ({pnlPct >= 0 ? "+" : ""}
              {pnlPct.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>

      {/* Committed progress bar */}
      <div
        className="h-1.5 overflow-hidden rounded-full"
        style={{
          background:
            "color-mix(in srgb, var(--color-text3) 18%, transparent)",
        }}
        aria-label={`Committed ${committedPct.toFixed(0)}% of allocation`}
      >
        <span
          className="block h-full"
          style={{
            width: `${Math.min(100, committedPct)}%`,
            background: "var(--color-yes)",
          }}
        />
      </div>

      {/* Actions */}
      {!cancelled && (
        <div className="flex flex-wrap gap-2">
          <Link
            href={`/portfolio/copy/${sub.id}`}
            className="rounded-md border px-3 py-1.5 text-[12px] font-medium"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-text2)",
            }}
          >
            Activity
          </Link>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md border px-3 py-1.5 text-[12px] font-medium"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-text2)",
            }}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onPauseResume}
            className="rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-text2)",
            }}
          >
            {busy ? "…" : sub.paused ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onUnsubscribe}
            className="ml-auto rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
            style={{
              borderColor: "var(--color-no)",
              color: "var(--color-no)",
            }}
          >
            Unsubscribe
          </button>
        </div>
      )}
    </div>
  );
}

function CopySkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="rounded-xl border p-4"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
          }}
        >
          <div className="space-y-3">
            <span
              className="block h-4 w-1/3 rounded"
              style={{ background: "var(--color-surface2)" }}
            />
            <span
              className="block h-3 w-2/3 rounded"
              style={{ background: "var(--color-surface2)" }}
            />
            <span
              className="block h-3 w-1/2 rounded"
              style={{ background: "var(--color-surface2)" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatRelative(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
